import { useTranslation } from "react-i18next"

import {
  useApolloClient,
  useMutation as useApolloMutation
} from "@apollo/client"

import { callMutation } from "shared/api/callMutation"
import {
  evictParentPricesAfterDeletion,
  evictProjectPrice
} from "shared/api/price/evictPriceCache"
import { invalidateParentPricesAfterDeletion } from "shared/api/price/modifyPriceCache"
import {
  DeleteResponseTypename,
  MutationSchema,
  MutationTypename
} from "shared/api/types"
import { useProjectRequestMonitor } from "shared/lib/request-monitoring/useProjectRequestMonitor"
import { useEvent } from "shared/lib/useEvent"
import { useRequestToasts } from "shared/lib/useRequestToasts"
import { ApolloFetchPolicy } from "shared/types/ApolloFetchPolicy"
import { FloorTypename } from "shared/types/Project"
import { DELETE_FLOOR } from "shared/types/ProjectMutations"

import {
  DeleteFloorDocument,
  DeleteFloorMutation,
  DeleteFloorMutationVariables
} from "entities/floors/graphql/mutations.generated"
import { removeProjectChildFromProject } from "entities/project-children/lib/removeProjectChildFromProject"
import { resolveProjectChildParent } from "entities/project-children/lib/resolveProjectChildParent"
import { useProjectChildrenLazyQuery } from "entities/projects/graphql/queries.generated"
import z from "zod"

import { evictFloor } from "../lib/evictFloor"

const DeleteFloorVariablesSchema = z.object({
  id: z.string()
})

export function useDeleteFloor() {
  const { t } = useTranslation()
  const { cache: apolloCache } = useApolloClient()
  const [fetchProject] = useProjectChildrenLazyQuery({
    fetchPolicy: ApolloFetchPolicy.NETWORK_ONLY
  })
  const { raiseLoadingToast } = useRequestToasts({
    messages: {
      loading: t("Request.Floor.DELETE_FLOOR.Start"),
      success: t("Request.Floor.DELETE_FLOOR.Success"),
      failure: t("Request.Floor.DELETE_FLOOR.Failure")
    }
  })
  const startProjectRequest = useProjectRequestMonitor()

  const [apolloMutate, rest] = useApolloMutation<
    DeleteFloorMutation,
    DeleteFloorMutationVariables
  >(DeleteFloorDocument)

  const deleteFloor = useEvent(
    async (variables: DeleteFloorMutationVariables, projectID: string) => {
      const parent = await resolveProjectChildParent({
        projectID,
        childID: variables.id,
        cache: apolloCache,
        refetch: () => fetchProject({ variables: { id: projectID } })
      })

      const requestContext = {
        projectID,
        itemID: variables.id,
        itemType: FloorTypename,
        parentID: parent.id,
        parentType: parent.__typename
      }

      const { raiseSuccessToast, raiseErrorToast } = raiseLoadingToast()
      const completeProjectRequest = startProjectRequest({
        projectID,
        requestType: DELETE_FLOOR
      })

      evictFloor(variables.id, apolloCache)
      invalidateParentPricesAfterDeletion({
        cache: apolloCache,
        context: requestContext
      })

      return callMutation(apolloMutate, {
        variables,
        transform: data => ({ success: data.floor.success }),
        schemas: {
          input: DeleteFloorVariablesSchema,
          response: MutationSchema.extend({
            floor: z.object({
              __typename: z.literal(DeleteResponseTypename),
              success: z.boolean()
            })
          })
        },
        apolloOptions: {
          optimisticResponse: {
            __typename: MutationTypename,
            floor: { __typename: DeleteResponseTypename, success: true }
          },
          update: cache => {
            removeProjectChildFromProject(cache, projectID, variables.id)
          },
          onCompleted: () => {
            evictFloor(variables.id, apolloCache)
            evictParentPricesAfterDeletion({
              cache: apolloCache,
              context: requestContext
            })
            completeProjectRequest()
            fetchProject({ variables: { id: projectID } })
            raiseSuccessToast()
          },
          onError: () => {
            evictProjectPrice(projectID, apolloCache, true)
            fetchProject({ variables: { id: projectID } })
            completeProjectRequest()
            raiseErrorToast()
          }
        }
      })
    }
  )
  return [deleteFloor, rest] as [typeof deleteFloor, typeof rest]
}
